import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Crosshair, RotateCcw, Save } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { DEFAULT_ALIGN, nudge } from "@/lib/geo";

const Row = ({ label, value, children }) => (
  <div className="grid grid-cols-[86px_1fr_56px] items-center gap-2">
    <span className="font-mono text-[10px] uppercase text-slate-400">{label}</span>
    {children}
    <span className="font-mono text-xs text-right text-[#FFB800]">{value}</span>
  </div>
);

const btn = "p-1.5 border border-[#282D3D] rounded-sm text-slate-300 hover:text-white hover:border-slate-500 transition-colors duration-200";

export const AlignControls = ({ align, setAlign, track, picking, setPicking, onSave }) => {
  const { t } = useT();
  const move = (e, n) => setAlign(nudge(align, track, e, n));
  return (
    <div data-testid="align-controls" className="space-y-3 border-t border-[#282D3D] pt-3 mt-3">
      <Row label={t("rotation")} value={`${align.rotation.toFixed(1)}°`}>
        <Slider data-testid="align-rotation-slider" min={-180} max={180} step={0.5} value={[align.rotation]} onValueChange={([v]) => setAlign({ ...align, rotation: v })} />
      </Row>
      <Row label={t("scale")} value={`${(align.scale * 100).toFixed(1)}%`}>
        <Slider data-testid="align-scale-slider" min={0.8} max={1.2} step={0.002} value={[align.scale]} onValueChange={([v]) => setAlign({ ...align, scale: v })} />
      </Row>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase text-slate-400 w-[86px]">{t("move_1m")}</span>
        <button type="button" data-testid="align-west" className={btn} onClick={() => move(-1, 0)}><ArrowLeft className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="align-north" className={btn} onClick={() => move(0, 1)}><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="align-south" className={btn} onClick={() => move(0, -1)}><ArrowDown className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="align-east" className={btn} onClick={() => move(1, 0)}><ArrowRight className="w-3.5 h-3.5" /></button>
        <label className="flex items-center gap-2 ml-auto font-mono text-[10px] uppercase text-slate-400">
          <Switch data-testid="align-flip-switch" checked={align.flip} onCheckedChange={(v) => setAlign({ ...align, flip: v })} /> {t("mirror")}
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" data-testid="align-pick-btn" onClick={() => setPicking(!picking)}
          className={`rounded-sm bg-transparent h-9 ${picking ? "border-[#FFB800] text-[#FFB800]" : "border-[#282D3D]"} hover:bg-[#181B26]`}>
          <Crosshair className="w-4 h-4 mr-1.5" /> {picking ? t("click_on_map") : t("place_here")}
        </Button>
        <Button type="button" variant="ghost" data-testid="align-reset-btn" onClick={() => setAlign(DEFAULT_ALIGN)} className="h-9"><RotateCcw className="w-4 h-4 mr-1.5" /> {t("reset")}</Button>
        <Button type="button" data-testid="align-save-btn" onClick={onSave} className="ml-auto rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide h-9">
          <Save className="w-4 h-4 mr-1.5" /> {t("save_alignment")}
        </Button>
      </div>
    </div>
  );
};
